import { Paper, TextField } from "@mui/material";
import { createTheme, responsiveFontSizes } from "@mui/material/styles";
import { Localization } from "@mui/material/locale";
import { Shadows } from "@mui/material/styles/shadows";
import { localeDirections } from "@redux/actions/locale";
import * as typographies from "./typography";
import * as colors from "./colors";
import type { IContext } from "./colors";
import "typeface-nunito-sans";
import "./styles.css";
import { TypographyStyleOptions } from "@mui/material/styles/createTypography";

declare module "@mui/material/styles" {
  interface SimplePaletteColorOptions {
    shade?: string;
  }
  interface Palette {
    tertiary: Palette["primary"];
    context: IContext;
  }
  interface PaletteOptions {
    tertiary: PaletteOptions["primary"];
    context: IContext;
  }
  interface TypographyVariants {
    caption1: TypographyStyleOptions;
    caption2: TypographyStyleOptions;
    caption3: TypographyStyleOptions;
    subtitle3: TypographyStyleOptions;
    code: TypographyStyleOptions;
  }
  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    caption1?: TypographyStyleOptions;
    caption2?: TypographyStyleOptions;
    caption3?: TypographyStyleOptions;
    subtitle3?: TypographyStyleOptions;
    code?: TypographyStyleOptions;
  }
  interface BreakpointOverrides {
    xs: true; // removes the `xs` breakpoint
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xx: true;
  }
}
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    caption1: true;
    caption2: true;
    caption3: true;
    caption: false;
    subtitle3: true;
    code: true;
  }
}

let shadows: Shadows | undefined = [...(Array(25).fill("none") as Shadows)];
shadows[0] = "none";
shadows[1] = "0px 0px 2px 1px rgba(0, 0, 0, 0.2)";
shadows[2] = "1px 4px 12px 1px rgba(0, 0, 0, 0.2)";
shadows[3] = "2px 6px 10px 1px rgba(0, 0, 0, 0.2)";
shadows[4] = "3px 8px 12px 1px rgba(0, 0, 0, 0.25)";
shadows[5] = "4px 10px 16px 1.5px rgba(0, 0, 0, 0.25)";
shadows[6] = "6px 12px 18px 0.5px rgba(0, 0, 0, 0.5)";

const theme = (locale: Localization, direction: localeDirections) =>
  createTheme(
    {
      direction: direction,
      spacing: 2,
      palette: {
        mode: "light",
        primary: colors.primary,
        secondary: colors.secondary,
        tertiary: colors.tertiary,
        error: colors.error,
        info: colors.info,
        success: colors.success,
        warning: colors.warning,
        context: colors.context,
        grey: colors.grey,
        background: colors.background,
      },
      typography: typographies,
      shape: {
        borderRadius: 4,
      },
      components: {
        MuiSkeleton: {
          styleOverrides: {
            root: {
              transform: "scale(1,1)",
            },
          },
        },
        MuiDivider: {
          styleOverrides: {
            root: {
              ...typographies.caption2,
              color: colors.context.light,
            },
            textAlignLeft: {
              "&:before": {
                width: "0%",
              },
              "&:after": {
                marginLeft: "10px",
                width: "calc(100% - 10px)",
              },
            },
            textAlignRight: {
              "&:before": {
                marginRight: "10px",
                width: "calc(100% - 10px)",
              },
              "&:after": {
                width: "0%",
              },
            },
          },
        },
        MuiTextField: {
          defaultProps: {
            size: "medium",
            fullWidth: true,
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            notchedOutline: {
              border: `1px solid ${colors.grey[300]}`,
            },
          },
        },
        MuiSelect: {
          defaultProps: {
            size: "medium",
            fullWidth: true,
            MenuProps: {
              PaperProps: { elevation: 2, sx: { marginTop: 2 } },
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            sizeSmall: (params) => ({
              ...(params.ownerState.multiline
                ? { height: 24 }
                : { minHeight: 24 }),
            }),
            root: (params) => ({
              backgroundColor: colors.grey[50],
              borderRadius: "5px !important",
              padding: "5px 10px !important",
              "&.Mui-focused": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderWidth: "1px !important",
                },
              },
              ...(params.ownerState.multiline
                ? { minHeight: 30 }
                : { height: 30 }),
            }),
            input: {
              padding: "0 !important",
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            sizeSmall: {
              transform: "translate(14px, 6px) scale(1)",
            },
            root: {
              transform: "translate(14px, 6px) scale(1)",
            },
            shrink: {
              transform: "translate(14px, -9px) scale(.75)",
            },
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: {
              padding: "16px",
              paddingBottom: 0,
            },
          },
          defaultProps: {
            typography: "subtitle1",
          },
        },
        MuiIcon: {
          styleOverrides: {
            root: {
              fill: colors.grey[200],
            },
          },
        },
        MuiDialogContent: {
          styleOverrides: {
            root: {
              padding: "16px",
              paddingTop: "16px !important",
            },
          },
        },
        MuiDialogActions: {
          styleOverrides: {
            root: {
              padding: "16px",
              paddingTop: 0,
            },
          },
        },
        MuiPaper: {
          defaultProps: {
            elevation: 0,
          },
        },
        MuiSvgIcon: {
          defaultProps: {
            fontSize: "small",
          },
          styleOverrides: {
            fontSizeSmall: {
              fontSize: "17px !important",
            },
          },
        },
        MuiIconButton: {
          defaultProps: {
            size: "small",
          },
          styleOverrides: {
            sizeSmall: {
              padding: 3,
            },
          },
        },
        MuiCheckbox: {
          defaultProps: {
            size: "small",
          },
        },
        MuiAutocomplete: {
          defaultProps: {
            size: "medium",
            fullWidth: true,
            PaperComponent: (params) => <Paper {...params} elevation={1} />,
            renderInput: (params) => <TextField {...params} />,
            ChipProps: { size: "small" },
          },
          styleOverrides: {
            tagSizeSmall: (params) => {
              if (params.ownerState.size === "small")
                return {
                  height: 18,
                  fontSize: 11,
                  "& .MuiSvgIcon-fontSizeSmall": {
                    marginRight: 1,
                  },
                };
            },
            input: (params) => ({
              marginTop: params.ownerState.size === "small" ? 0 : -11,
            }),
            endAdornment: {
              display: "flex",
              alignItems: "center",
              height: 26,
              top: "calc(50% - 13px)",
            },
          },
        },
        MuiButton: {
          defaultProps: {
            variant: "contained",
            color: "primary",
            size: "small",
            disableElevation: true,
          },
          styleOverrides: {
            text: {
              backgroundColor: colors.grey[200],
              color: colors.context.dark,
              "&:hover": {
                backgroundColor: colors.grey[300],
              },
            },
            containedInherit: {
              color: colors.grey[700],
              "&:hover": {
                color: colors.grey[600],
              },
            },
            sizeSmall: {
              fontSize: "13px",
              fontWeight: 500,
              lineHeight: "22px",
              padding: "3px 12px",
              letterSpacing: "0.46px",
              textTransform: "capitalize",
            },
            sizeMedium: {
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "24px",
              padding: "6px 16px",
              letterSpacing: "0.46px",
              textTransform: "capitalize",
            },
            sizeLarge: {
              fontSize: "15px",
              fontWeight: 500,
              lineHeight: "26px",
              padding: "8px 22px",
              letterSpacing: "0.46px",
              textTransform: "capitalize",
            },
          },
        },
        MuiList: {
          styleOverrides: {
            root: {
              borderRadius: "4px",
              padding: "5px 0",
            },
          },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              padding: "2px 4px",
              "&:hover": {
                backgroundColor: colors.grey[200],
              },
            },
          },
        },
        MuiListItemIcon: {
          styleOverrides: {
            root: {
              display: "flex",
              justifyContent: "center",
              minWidth: 25,
              padding: "4px",
              marginInlineEnd: "5px",
            },
          },
        },
        MuiListItemText: {
          styleOverrides: {
            root: { padding: "4px", margin: "0" },
          },
        },
      },
      shadows,
    },
    locale
  );

export const themeGenerator = (
  locale: Localization,
  direction: localeDirections
) => responsiveFontSizes(theme(locale, direction));
